import type React from 'react'

export type MetaTag = React.JSX.IntrinsicElements['meta']
export type LinkTag = React.JSX.IntrinsicElements['link']
export type ScriptTag = React.JSX.IntrinsicElements['script']

export type HeadResult = {
  meta: MetaTag[]
  links?: LinkTag[]
  scripts?: ScriptTag[]
}

export const SITE_NAME = 'Mátevolno'

export const DEFAULT_TITLE = 'Mátevolno – last-minute ubytování na výjimečných místech'

export const DEFAULT_DESCRIPTION =
  'Objevte právě uvolněné termíny ubytování na výjimečných místech po celém Česku. Chalupy, chaty, penziony i hotely na poslední chvíli – rychle a bez starostí.'

const FALLBACK_ORIGIN = 'https://www.matevolno.cz'

/** Contact details, kept in one place so JSON-LD and the contact page agree. */
export const CONTACT_PHONE = '+420605131713'
export const CONTACT_EMAIL = 'info@matevolno.cz'

/**
 * Absolute origin of the current deployment. Used to build absolute URLs for
 * canonical links and Open Graph tags. Falls back to the production domain when
 * `window` is unavailable.
 */
export function getSiteOrigin(): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin
  }
  return FALLBACK_ORIGIN
}

export function coverImageUrl(origin: string = getSiteOrigin()): string {
  return `${origin}/img/cover.webp`
}

/** Build an absolute canonical URL from a root-relative path. */
export function canonicalUrl(path: string, origin: string = getSiteOrigin()): string {
  return path === '/' ? origin : `${origin}${path}`
}

/**
 * Serialize JSON-LD for inlining inside a <script> tag. Escapes `<` so payload
 * content (e.g. a description) can never break out of the script element.
 */
export function serializeJsonLd(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

/** Points agents at the page's plain-text twin (see src/features/agent). */
export function markdownAlternate(href: string): LinkTag {
  return { rel: 'alternate', type: 'text/markdown', href }
}

/** Build an `application/ld+json` script tag from a JSON-LD object. */
export function jsonLdScript(data: Record<string, unknown>): ScriptTag {
  return { type: 'application/ld+json', children: serializeJsonLd(data) }
}

export function breadcrumbJsonLd(items: { name: string; item: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  }
}

export function collectionPageJsonLd(name: string, description: string, url: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url,
    inLanguage: 'cs-CZ',
  }
}

/** `robots` meta that keeps a page out of the index (used on private pages). */
export function noindexMeta(): MetaTag[] {
  return [{ name: 'robots', content: 'noindex, follow' }]
}

/**
 * Site-wide Organization + WebSite entities, emitted once on the homepage.
 * Google associates them with the whole domain; there is no free-text search on
 * the site, so no `SearchAction` is declared (an unusable target hurts more than
 * it helps).
 */
export function organizationJsonLd(origin: string = getSiteOrigin()): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: SITE_NAME,
        url: origin,
        logo: `${origin}/img/logo_matevolno.svg`,
        image: coverImageUrl(origin),
        email: CONTACT_EMAIL,
        telephone: CONTACT_PHONE,
        areaServed: 'CZ',
        sameAs: [
          'https://www.facebook.com/matevolno/',
          'https://www.instagram.com/matevolno/',
          'https://www.tiktok.com/@matevolno',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: CONTACT_PHONE,
          email: CONTACT_EMAIL,
          contactType: 'customer support',
          areaServed: 'CZ',
          availableLanguage: ['cs'],
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        url: origin,
        name: SITE_NAME,
        inLanguage: 'cs-CZ',
        publisher: { '@id': `${origin}/#organization` },
      },
    ],
  }
}

/**
 * Default site-wide head tags, applied on the root route. Page routes (e.g. the
 * accommodation detail) override the matching `name`/`property` tags; TanStack
 * Router keeps the leaf-most value per attribute.
 */
export function defaultHeadMeta(): MetaTag[] {
  const origin = getSiteOrigin()
  const image = coverImageUrl()

  return [
    { title: DEFAULT_TITLE },
    { name: 'description', content: DEFAULT_DESCRIPTION },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'cs_CZ' },
    { property: 'og:url', content: origin },
    { property: 'og:title', content: DEFAULT_TITLE },
    { property: 'og:description', content: DEFAULT_DESCRIPTION },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/webp' },
    { property: 'og:image:alt', content: SITE_NAME },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:url', content: origin },
    { property: 'twitter:title', content: DEFAULT_TITLE },
    { property: 'twitter:description', content: DEFAULT_DESCRIPTION },
    { property: 'twitter:image', content: image },
    { property: 'twitter:creator', content: '@matevolno.cz' },
    { property: 'twitter:site', content: '@matevolno.cz' },
  ]
}

/**
 * Meta descriptions over ~160 chars get cut or rewritten by Google. Page
 * perexes double as descriptions and are often longer, so trim to the last
 * full sentence that fits (falling back to a word boundary + ellipsis when
 * the first sentence alone is too long).
 */
export function truncateDescription(text: string, maxLength = 160): string {
  if (text.length <= maxLength) return text
  const slice = text.slice(0, maxLength)
  const sentenceEnd = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('! '), slice.lastIndexOf('? '))
  if (sentenceEnd >= 60) return slice.slice(0, sentenceEnd + 1)
  return `${slice.slice(0, slice.lastIndexOf(' ')).trimEnd()}…`
}

/**
 * Generic per-page head builder for simple static pages (search, content
 * pages). Emits title, description, canonical, Open Graph and Twitter tags.
 * Set `noindex` for pages that must stay out of the index (no canonical is
 * emitted then, to avoid a conflicting canonical + noindex signal).
 */
export function buildPageHead(options: {
  title: string
  description: string
  path: string
  ogType?: string
  noindex?: boolean
  origin?: string
}): HeadResult {
  const { title, path, ogType = 'website', noindex = false, origin = getSiteOrigin() } = options
  const description = truncateDescription(options.description)
  const url = canonicalUrl(path, origin)
  const image = coverImageUrl(origin)

  const meta: MetaTag[] = [
    { title },
    { name: 'description', content: description },
    ...(noindex ? noindexMeta() : []),
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'cs_CZ' },
    { property: 'og:url', content: url },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/webp' },
    { property: 'og:image:alt', content: SITE_NAME },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:url', content: url },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
  ]

  return {
    meta,
    links: noindex ? [] : [{ rel: 'canonical', href: url }],
  }
}
